import { createTheme } from '@mui/material/styles'
import { deepmerge } from '@mui/utils'
import { IS_NEW_LAYOUT_ENABLED } from './../constants/layoutConstant'

const breakpoints = {
    values: {
        xs: 0,
        sm: 390,
        smd: 768,
        md: 991,
        lmd: 1024,
        lg: 1440
    }
}
const commonColors = {
    black: {
        main: '#000000',
        1: '#000000',
        2: 'rgba(0, 0, 0, 0.7)',
        3: 'rgba(0, 0, 0, 0.5)',
        4: 'rgba(0, 0, 0, 0.3)',
        5: 'rgba(0, 0, 0, 0.1)'
    },
    neutralBlack: {
        main: '#010203',
        dark: '#1F3047'
    },
    blueAccent1: {
        light: '#0093F0',
        main: '#0068D0',
        dark: '#0068D0'
    },
    blueAccent2: {
        light: '#0079EB',
        main: '#0142BD',
        dark: '#0142BD'
    },
    caution: {
        light: '#DA7029',
        main: '#BA4E00',
        dark: '#BA4E00'
    },
    charcoal: {
        main: '#333333'
    },
    error: {
        light: '#FF3030',
        main: '#D62D0A',
        dark: '#D62D0A',
        darker: '#BF152C'
    },
    fordBlue: {
        main: '#00095B'
    },
    fordTwilight: {
        main: '#00142E'
    },
    fordGrabber: {
        main: '#1700F4'
    },
    gray: {
        1: '#F0F0F0',
        2: '#C5C5C5',
        3: '#9C9C9C',
        4: '#8C8C8C',
        5: '#5E5E5E',
        6: '#4D4D4D',
        7: '#E6E8EA',
        8: '#0000001A',
        9: '#FAFAFA',
        pale: '#F2F2F2',
        main: '#F2F3F5',
        light: '#666666',
        dark: '#B2B2B2',
        think: '#E5E5E5',
        11: '#FCF2E6',
        12: '#E5F3E6'
    },
    success: {
        light: '#29A429',
        main: '#008200',
        dark: '#008200'
    },
    surface: {
        main: '#F4F6FA'
    },
    white: {
        1: '#FFFFFF',
        2: '#FFFFFFCC',
        3: '#FFFFFF99',
        4: '#FFFFFF66',
        5: '#FFFFFF33',
        main: '#FFFFFF'
    },
    alert: {
        main: '#BA4E00',
        info: '#B16201'
    },
    stroke: {
        main: '#9CA3AD'
    },
    link: {
        light: '#0562D2',
        main: '#066FEF',
        victoria: '#044EA7'
    }
}

const baseTitle = {
    fontSize: '24px',
    lineHeight: '32px'
}
const baseSubTitle = {
    fontSize: '20px',
    lineHeight: '24px'
}
const baseBody1 = {
    fontSize: '16px',
    lineHeight: '24px'
}
const baseBody2 = {
    fontSize: '14px',
    lineHeight: '24px'
}
const baseBody3 = {
    fontSize: '12px',
    lineHeight: '16px'
}

const baseCaption = {
    fontSize: '11px',
    lineHeight: '16px'
}

const rootTheme = createTheme(
    deepmerge(
        {},
        {
            spacing: 4,
            breakpoints,
            palette: {
                mode: 'light',
                primary: {
                    main: commonColors.blueAccent1.main,
                    dark: commonColors.fordGrabber.main,
                    contrastText: commonColors.white.main
                },
                secondary: {
                    main: commonColors.neutralBlack.main,
                    dark: commonColors.black[3],
                    contrastText: commonColors.white.main
                },
                tertiary: {
                    ...commonColors.fordTwilight
                },
                background: {
                    default: commonColors.white.main,
                    paper: commonColors.surface.main
                },
                error: {
                    ...commonColors.error
                },
                warning: {
                    ...commonColors.caution
                },
                success: {
                    ...commonColors.success
                },
                text: {
                    primary: commonColors.fordTwilight.main,
                    secondary: commonColors.gray[6],
                    disabled: commonColors.black[4]
                },
                action: {
                    disabled: commonColors.black[4],
                    disabledBackground: commonColors.black[4],
                    hover: commonColors.blueAccent2.dark,
                    active: commonColors.fordTwilight.main
                },
                common: {
                    ...commonColors
                },
                divider: commonColors.black[5]
            },
            typography: {
                fontFamily: ['Ford F-1', 'sans-serif'].join(','),
                largeDisplay1: {
                    fontSize: '70px',
                    lineHeight: '78px'
                },
                largeDisplay2: {
                    fontSize: '62px',
                    lineHeight: '70px'
                },
                largeDisplay3: {
                    fontSize: '54px',
                    lineHeight: '62px'
                },
                largeHeadline1: {
                    fontSize: '46px',
                    lineHeight: '52px'
                },
                largeHeadline2: {
                    fontSize: '42px',
                    lineHeight: '48px'
                },
                largeHeadline3: {
                    fontSize: '38px',
                    lineHeight: '40px'
                },
                smallDisplay1: {
                    fontSize: '48px',
                    lineHeight: '54px'
                },
                smallDisplay2: {
                    fontSize: '44px',
                    lineHeight: '50px'
                },
                smallDisplay3: {
                    fontSize: '40px',
                    lineHeight: '46px'
                },
                smallHeadline0: {
                    fontSize: '36px',
                    lineHeight: '44px'
                },
                smallHeadline1: {
                    fontSize: '34px',
                    lineHeight: '40px'
                },
                smallHeadline2: {
                    fontSize: '32px',
                    lineHeight: '38px'
                },
                smallHeadline3: {
                    fontSize: '28px',
                    lineHeight: '34px'
                },
                smallHeadline4: {
                    fontSize: '22px',
                    lineHeight: '34px'
                },
                lightTitle: {
                    ...baseTitle,
                    fontWeight: 300
                },
                regularTitle: {
                    ...baseTitle
                },
                mediumTitle: {
                    ...baseTitle,
                    fontWeight: 500
                },
                boldTitle: {
                    ...baseTitle,
                    fontWeight: 700
                },
                lightSubTitle: {
                    ...baseSubTitle,
                    fontWeight: 300
                },
                regularSubTitle: {
                    ...baseSubTitle
                },
                mediumSubTitle: {
                    ...baseSubTitle,
                    fontWeight: 500
                },
                boldSubTitle: {
                    ...baseSubTitle,
                    fontWeight: 700
                },
                lightBody1: {
                    ...baseBody1,
                    fontWeight: 300
                },
                regularBody1: {
                    ...baseBody1
                },
                mediumBody1: {
                    ...baseBody1,
                    fontWeight: 500
                },
                semiBoldBody1: {
                    ...baseBody1,
                    fontWeight: 600
                },
                boldBody1: {
                    ...baseBody1,
                    fontWeight: 700
                },
                lightBody2: {
                    ...baseBody2,
                    fontWeight: 300
                },
                regularBody2: {
                    ...baseBody2
                },
                mediumBody2: {
                    ...baseBody2,
                    fontWeight: 500
                },
                semiBoldBody2: {
                    ...baseBody2,
                    fontWeight: 600
                },
                boldBody2: {
                    ...baseBody2,
                    fontWeight: 700
                },
                lightBody3: {
                    ...baseBody3,
                    fontWeight: 300
                },
                regularBody3: {
                    ...baseBody3
                },
                mediumBody3: {
                    ...baseBody3,
                    fontWeight: 500
                },
                semiBoldBody3: {
                    ...baseBody3,
                    fontWeight: 500
                },
                boldBody3: {
                    ...baseBody3,
                    fontWeight: 700
                },
                lightCaption: {
                    ...baseCaption,
                    fontWeight: 300
                },
                regularCaption: {
                    ...baseCaption
                },
                mediumCaption: {
                    ...baseCaption,
                    fontWeight: 500
                },
                boldCaption: {
                    ...baseCaption,
                    fontWeight: 700
                },
                decorative: {
                    fontSize: '8px',
                    lineHeight: '8px'
                }
            },
            components: {
                MuiTypography: {
                    defaultProps: {
                        variantMapping: {
                            largeDisplay1: 'div',
                            largeDisplay2: 'div',
                            largeDisplay3: 'div',
                            largeHeadline1: 'div',
                            largeHeadline2: 'div',
                            largeHeadline3: 'div',
                            smallDisplay1: 'div',
                            smallDisplay2: 'div',
                            smallDisplay3: 'div',
                            smallHeadline0: 'div',
                            smallHeadline1: 'div',
                            smallHeadline2: 'div',
                            smallHeadline3: 'div',
                            smallHeadline4: 'div',
                            lightTitle: 'div',
                            regularTitle: 'div',
                            mediumTitle: 'div',
                            boldTitle: 'div',
                            lightSubTitle: 'div',
                            regularSubTitle: 'div',
                            mediumSubTitle: 'div',
                            boldSubTitle: 'div',
                            lightBody1: 'div',
                            regularBody1: 'div',
                            mediumBody1: 'div',
                            semiBoldBody1: 'div',
                            boldBody1: 'div',
                            lightBody2: 'div',
                            regularBody2: 'div',
                            mediumBody2: 'div',
                            semiBoldBody2: 'div',
                            boldBody2: 'div',
                            lightBody3: 'div',
                            regularBody3: 'div',
                            mediumBody3: 'div',
                            semiBoldBody3: 'div',
                            boldBody3: 'div',
                            lightCaption: 'div',
                            regularCaption: 'div',
                            mediumCaption: 'div',
                            boldCaption: 'div',
                            decorative: 'div'
                        }
                    }
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            ...baseBody2,
                            borderRadius: 20,
                            boxShadow: 'none',
                            fontWeight: 700,
                            padding: '8px 20px',
                            textTransform: 'none'
                        },
                        endIcon: {
                            display: 'flex',
                            justifyContent: 'center',
                            marginRight: 0,
                            width: 16,
                            height: 16,
                            'svg:nth-of-type(1)': {
                                fontSize: 'initial'
                            }
                        },
                        startIcon: {
                            display: 'flex',
                            justifyContent: 'center',
                            marginLeft: 0,
                            width: 16,
                            height: 16,
                            'svg:nth-of-type(1)': {
                                fontSize: 'initial'
                            }
                        },
                        contained: {
                            '&:hover': {
                                boxShadow: 'none',
                                backgroundColor: commonColors.link.victoria
                            },
                            '&.Mui-disabled': {
                                backgroundColor: commonColors.black[4],
                                color: commonColors.white[3]
                            },
                            '&.Mui-focusVisible': {
                                border: '4px double',
                                borderRadius: 24,
                                boxShadow: 'none'
                            }
                        },
                        outlined: {
                            backgroundColor: 'unset',
                            color: commonColors.link.light,
                            '&:hover': {
                                borderColor: `${commonColors.link.victoria} !important`,
                                color: `${commonColors.link.victoria} !important`,
                                backgroundColor: 'unset',
                                borderWidth: 1
                            },
                            '&.Mui-focusVisible': {
                                border: '4px double',
                                borderRadius: 24,
                                boxShadow: 'none'
                            }
                        },
                        outlinedPrimary: {
                            borderColor: commonColors.link.light,
                            '&:hover': {
                                borderColor: commonColors.blueAccent2.dark,
                                color: commonColors.blueAccent2.dark
                            }
                        },
                        outlinedSecondary: {
                            borderColor: commonColors.neutralBlack.main,
                            '&:hover': {
                                borderColor: commonColors.black[3],
                                color: commonColors.black[3]
                            }
                        },
                        text: {
                            '&:hover': {
                                backgroundColor: 'unset',
                                color: commonColors.link.victoria
                            },
                            '&.Mui-focusVisible': {
                                border: '1px solid',
                                boxShadow: 'none',
                                borderRadius: 24
                            }
                        },
                        textPrimary: {
                            '&:hover': {
                                color: commonColors.blueAccent2.dark
                            }
                        },
                        textSecondary: {
                            '&:hover': {
                                color: commonColors.black[3]
                            }
                        }
                    }
                },
                MuiCheckbox: {
                    styleOverrides: {
                        root: {
                            '&:hover': {
                                color: commonColors.blueAccent1.dark
                            },
                            '&.Mui-checked:hover': {
                                color: commonColors.blueAccent2.dark
                            },
                            '&.Mui-disabled': {
                                color: commonColors.gray[2]
                            }
                        }
                    }
                },
                MuiAccordion: {
                    styleOverrides: {
                        root: {
                            backgroundColor: 'inherit',
                            borderBottom: 'none',
                            borderRadius: 'inherit',
                            paddingBottom: 16,
                            '&.Mui-expanded': {
                                borderBottom: 'none',
                                minHeight: 'initial',
                                margin: 0
                            },
                            '&:before': {
                                opacity: 0
                            }
                        }
                    }
                },
                MuiAccordionSummary: IS_NEW_LAYOUT_ENABLED
                    ? {
                          styleOverrides: {
                              root: {
                                  alignItems: 'baseline',
                                  gap: 8,
                                  padding: 0,
                                  minHeight: 'initial',
                                  color: commonColors.link.light,
                                  '&.Mui-expanded': {
                                      borderBottom: 'none',
                                      minHeight: 'initial',
                                      margin: 0
                                  }
                              },
                              content: {
                                  margin: '0px',
                                  gap: 8,
                                  alignItems: 'center',
                                  '&.Mui-expanded': {
                                      margin: '0px',
                                      '& svg': {
                                          transform: 'rotate(180deg)',
                                          marginTop: '-3px'
                                      }
                                  },
                                  '& svg': {
                                      transition: 'all 0.5s ease'
                                  }
                              },
                              expandIconWrapper: {
                                  color: 'inherit'
                              }
                          }
                      }
                    : {
                          styleOverrides: {
                              root: {
                                  alignItems: 'baseline',
                                  borderBottom: `1px solid ${commonColors.black[5]}`,
                                  gap: 8,
                                  padding: 0,
                                  marginLeft: 12,
                                  marginRight: 12,
                                  '&.Mui-expanded': {
                                      borderBottom: 'none',
                                      color: commonColors.blueAccent1.dark
                                  },
                                  [`@media (min-width: ${breakpoints.values.md}px)`]: {
                                      gap: 12,
                                      marginLeft: 24,
                                      marginRight: 24
                                  }
                              },
                              content: {
                                  margin: '24px 0px'
                              },
                              expandIconWrapper: {
                                  color: 'inherit'
                              }
                          }
                      },
                MuiAccordionDetails: IS_NEW_LAYOUT_ENABLED
                    ? {
                          styleOverrides: {
                              root: {
                                  color: commonColors.gray[6],
                                  display: 'grid',
                                  padding: 0
                              }
                          }
                      }
                    : {
                          styleOverrides: {
                              root: {
                                  borderTop: `2px solid ${commonColors.blueAccent1.dark}`,
                                  color: commonColors.gray[6],
                                  display: 'grid',
                                  padding: 0,
                                  margin: '0px 12px',
                                  [`@media (min-width: ${breakpoints.values.md}px)`]: {
                                      borderWidth: 1,
                                      margin: '0px 24px'
                                  }
                              }
                          }
                      },
                MuiIcon: {
                    styleOverrides: {
                        root: {
                            boxSizing: 'content-box',
                            padding: 3,
                            fontSize: '1.125rem'
                        }
                    }
                }
            }
        }
    )
)

export const lightTheme = createTheme(deepmerge({}, rootTheme))

export const darkTheme = createTheme(
    deepmerge(rootTheme, {
        palette: {
            mode: 'dark',
            primary: {
                main: commonColors.blueAccent1.light,
                dark: commonColors.blueAccent2.light,
                contrastText: commonColors.white.main
            },
            secondary: {
                main: commonColors.white.main,
                dark: commonColors.white[3],
                contrastText: commonColors.neutralBlack.main
            },
            background: {
                default: commonColors.fordTwilight.main
            },
            text: {
                primary: commonColors.white.main,
                secondary: commonColors.white.main,
                disabled: commonColors.white[4]
            },
            action: {
                disabled: commonColors.white[4],
                disabledBackground: commonColors.white[4],
                hover: commonColors.white.main,
                active: commonColors.white.main
            },
            divider: commonColors.white.main
        },
        components: {
            MuiButton: {
                styleOverrides: {
                    contained: {
                        '&.Mui-disabled': {
                            backgroundColor: commonColors.white[4],
                            color: commonColors.white[3]
                        }
                    },
                    containedSecondary: {
                        '&.Mui-disabled': {
                            color: commonColors.black[4]
                        }
                    },
                    outlinedPrimary: {
                        borderColor: commonColors.blueAccent1.light,
                        '&:hover': {
                            borderColor: commonColors.blueAccent2.light,
                            color: commonColors.blueAccent2.light
                        }
                    },
                    outlinedSecondary: {
                        borderColor: commonColors.white.main,
                        '&:hover': {
                            borderColor: commonColors.white[3],
                            color: commonColors.white[3]
                        }
                    },
                    textPrimary: {
                        '&:hover': {
                            color: commonColors.blueAccent2.light
                        }
                    },
                    textSecondary: {
                        '&:hover': {
                            color: commonColors.white[2]
                        }
                    }
                }
            },
            MuiCheckbox: {
                styleOverrides: {
                    root: {
                        '&.Mui-checked:hover': {
                            color: commonColors.blueAccent2.light
                        },
                        '&.Mui-disabled': {
                            color: commonColors.white[4]
                        }
                    }
                }
            },
            MuiAccordionDetails: {
                styleOverrides: {
                    root: {
                        color: commonColors.white.main
                    }
                }
            }
        }
    })
)
